/**
 * URL safety helpers for third-party user-supplied URLs.
 *
 * The backend stores listing photo URLs, source URLs, etc. from
 * third-party data providers (Realie, RentCast, Realtor.com) without
 * validation. If a malicious provider ever injected a
 * ``javascript:`` or ``data:`` scheme, rendering it directly in an
 * ``href`` or passing it to ``window.open`` would execute JS in the
 * user's context.
 *
 * ``rel="noopener noreferrer"`` helps with tab-nabbing but does NOT
 * prevent ``javascript:`` URLs from firing on click. React escapes
 * HTML content but does NOT sanitize URL schemes in ``href``/``src``
 * attributes.
 *
 * These helpers enforce a strict allowlist of known-safe schemes.
 */

// Schemes that are safe to render in href or pass to window.open.
// Everything else — javascript:, data:, vbscript:, file:, etc. — is
// rejected.
const SAFE_URL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * Return ``url`` if it's a safe, parseable URL; otherwise return null.
 *
 * Callers should use the ``|| null`` pattern to conditionally render,
 * e.g. ``{safeHref && <a href={safeHref}>…</a>}``.
 */
export function safeUrl(url) {
  if (typeof url !== 'string' || !url) return null;
  // Reject relative URLs that start with a disallowed scheme pattern.
  // Note: new URL() accepts scheme-less relative URLs when given a base,
  // which would mask javascript: URLs. Do a cheap prefix check first.
  const trimmed = url.trim();
  // Disallow protocol-less injection vectors:
  //   "javascript:alert(1)" → new URL would parse this with any base
  //   "javascript\n:alert(1)" → same
  //   " javascript:alert(1)" → trimmed above
  if (/^(javascript|data|vbscript|file):/i.test(trimmed)) return null;
  try {
    const parsed = new URL(trimmed, window.location.origin);
    if (!SAFE_URL_SCHEMES.has(parsed.protocol)) return null;
    return parsed.href;
  } catch {
    return null;
  }
}
